/**
 * Small hex-color helpers for themable UI (envelope paper, seals, accents).
 * Every function is total: anything that is not a hex color comes back
 * unchanged, so a bad value from config can never break rendering.
 */

const HEX_PATTERN = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** True when the value is a `#RGB` / `#RRGGBB` color. */
export function isHexColor(value: string | null | undefined): value is string {
  return typeof value === 'string' && HEX_PATTERN.test(value.trim());
}

/** Expand `#abc` to `#aabbcc`; returns the input for anything else. */
function toSixDigits(hex: string): string {
  const value = hex.trim();
  if (value.length !== 4) return value.toLowerCase();
  const [, r, g, b] = value;
  return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
}

/**
 * Shift a hex color toward black (negative percent) or white (positive),
 * e.g. `shadeHexColor('#f4efe6', -8)` for the shaded side of a paper fold.
 */
export function shadeHexColor(hex: string, percent: number): string {
  if (!isHexColor(hex)) return hex;
  const full = toSixDigits(hex);
  const amount = Math.max(-100, Math.min(100, percent)) / 100;
  const channel = (offset: number): string => {
    const value = parseInt(full.slice(offset, offset + 2), 16);
    const target = amount < 0 ? 0 : 255;
    const mixed = Math.round(value + (target - value) * Math.abs(amount));
    return Math.max(0, Math.min(255, mixed)).toString(16).padStart(2, '0');
  };
  return `#${channel(1)}${channel(3)}${channel(5)}`;
}

/** WCAG relative luminance of a `#RRGGBB` color (0 = black, 1 = white). */
function relativeLuminance(hex: string): number {
  const full = toSixDigits(hex);
  const linear = (offset: number): number => {
    const value = parseInt(full.slice(offset, offset + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * linear(1) + 0.7152 * linear(3) + 0.0722 * linear(5);
}

/** WCAG contrast ratio of white text on the given hex background. */
export function contrastWithWhite(hex: string): number {
  return 1.05 / (relativeLuminance(hex) + 0.05);
}

/**
 * Darken a hex color just enough for white text on it to meet `minContrast`
 * (4.5:1 by default — WCAG AA for the 12px badge labels this serves). A color
 * that already passes comes back as-is, so a strong configured color keeps its exact shade and only pale ones
 * (a light yellow, a mint green) are deepened. Non-hex input is returned unchanged.
 */
export function darkenForWhiteText(hex: string, minContrast = 4.5): string {
  if (!isHexColor(hex)) return hex;
  const full = toSixDigits(hex);
  if (contrastWithWhite(full) >= minContrast) return full;
  for (let percent = 2; percent < 100; percent += 2) {
    const shaded = shadeHexColor(full, -percent);
    if (contrastWithWhite(shaded) >= minContrast) return shaded;
  }
  return '#000000';
}
